import { DateTime } from '@ksyos/date-time';
import { store } from '../configureStore';
import AbortError from './AbortError';
import { SESSION_TIMEOUT_CHANGED } from './authentication/actions';
import FetchError from './FetchError';
import { TimeoutError } from './TimeoutError';

export interface IFetchResult {
    errorCode?: string;
    json?: any;
    message?: string;
    status: number;
}

export async function fetch(url: string, method: string, body?: any, signal?: AbortSignal) {
    const zxyToken = sessionStorage.getItem('zxy-token');
    let bodyString = undefined as any;
    if (body) {
        bodyString = JSON.stringify(body);
    }

    const response = await window.fetch(url, {
        body: bodyString,
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'zxy-token': zxyToken || '',
        },
        method: method,
        signal,
    });

    const sessionExpires = response.headers.get('X-Session-Expires');
    const serverTime = response.headers.get('X-Server-Time');
    if (sessionExpires && serverTime) {
        const timeDifference = DateTime.now().diff(DateTime.from(serverTime), 'seconds');
        const localSessionExpiresTime = DateTime.from(sessionExpires).add(timeDifference, 'seconds');
        store.dispatch({ timeout: localSessionExpiresTime, type: SESSION_TIMEOUT_CHANGED });
    }

    return response;
}

export async function getJson(url: string, signal?: AbortSignal) {
    return fetchJson(url, 'GET', undefined, undefined, undefined, signal);
}

export async function deleteJson(url: string, signal?: AbortSignal) {
    return fetchJson(url, 'DELETE', undefined, undefined, undefined, signal);
}

export async function postJson(url: string, body: unknown, signal?: AbortSignal) {
    return fetchJson(url, 'POST', body, undefined, undefined, signal);
}

export async function fetchJson(
    url: string,
    method: string,
    body?: unknown,
    allowedErrorCodes?: string[],
    allowedHTTPErrorCodes?: number[],
    signal?: AbortSignal
): Promise<IFetchResult> {
    let status = 0;

    const response = await fetch(url, method, body, signal);

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.indexOf('text/html') !== -1) {
        throw Error('Received HTML, does this route exist? (' + url + ')');
    }
    if (response.headers.get('Content-Length') && response.headers.get('Content-Length') === '0') {
        console.log('content lenght is 0', response);

        return { status: status };
    }
    status = response.status;

    let json;
    if (status !== 404) {
        json = await response.json();
    }

    if (signal && signal.aborted) {
        throw new AbortError();
    }

    if (status < 400) {
        return { json: json, status: status };
    }
    let code = 'DEFAULT';
    if (json && json.code) {
        code = json.code;
    }
    let message;
    if (json && json.message) {
        message = json.message;
    }
    if (status >= 400 && status < 500) {
        if (status === 401) {
            throw new TimeoutError();
        }
        if (allowedErrorCodes && allowedErrorCodes.includes(code)) {
            return { errorCode: code, message: message, status: status };
        }
        if (allowedHTTPErrorCodes && allowedHTTPErrorCodes.includes(status)) {
            return { errorCode: code, status: status };
        }
    }

    // Check if browser does not support unhandledrejection
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event
    if (typeof PromiseRejectionEvent === 'undefined') {
        Promise.reject(new FetchError(status, code));
    }
    throw new FetchError(status, code);
}

export class AbortableFetch {
    private controller = new AbortController();
    private signal: AbortSignal = this.controller.signal;

    public async getJson(url: string) {
        return getJson(url, this.signal);
    }

    public async deleteJson(url: string) {
        return deleteJson(url, this.signal);
    }

    public async postJson(url: string, body: unknown) {
        return postJson(url, body, this.signal);
    }

    public async fetchJson(
        url: string,
        method: string,
        body?: unknown,
        allowedErrorCodes?: string[],
        allowedHTTPErrorCodes?: number[]
    ) {
        return fetchJson(url, method, body, allowedErrorCodes, allowedHTTPErrorCodes, this.signal);
    }

    public abort() {
        this.controller.abort();
    }
}
